/**
 * Turning a WebXR light estimate into lighting the garden can actually use.
 *
 * The runtime hands back a measurement of the room: a direction to the
 * brightest light, its radiance, and spherical harmonics for the ambient. None
 * of that is in the units our scene is authored in, and an outdoor reading in
 * bright sun is enormous next to an indoor one. Applied raw it would blow the
 * garden out or black it in.
 *
 * So everything here is expressed as a **modest multiplier on the lighting we
 * already have**, hard-clamped at both ends. The scene keeps the look it was
 * designed with; the estimate only leans it toward the room.
 *
 * ## What is worth taking, and what is not
 *
 * The direction is the prize. Outdoors — which is where this was tested, in
 * Wakefield — getting the shadows to fall the same way as the real ones is
 * most of what makes the garden look like it is on the ground rather than
 * pasted over it. Absolute brightness is the part most likely to go wrong and
 * least likely to be noticed, so it moves least.
 *
 * These are plain-number functions rather than methods on a session, because
 * nothing about WebXR can be exercised from a desktop and this is the half
 * that can be checked.
 */

/** A frame's worth of real-world lighting, in our units. */
export interface LightSample {
  /**
   * Unit vector pointing *toward* the dominant light, in the session's
   * reference space. Null when the runtime offered no primary light.
   */
  direction: [number, number, number] | null;
  /** Multiplier on the directional light's authored intensity. */
  sun: number;
  /** Multiplier on the hemisphere light's authored intensity. */
  ambient: number;
  /** Colour to lean the key light toward, as linear RGB in 0–1. */
  colour: [number, number, number];
}

/**
 * How far the estimate is allowed to move the scene.
 *
 * Deliberately narrow. A wider band would track the room more faithfully and
 * would also mean a single bad reading — a phone pointed at a lamp, a sensor
 * still warming up — could wash the garden out entirely. The scene's authored
 * look is the thing being protected.
 */
const SUN_RANGE: [number, number] = [0.55, 1.45];
const AMBIENT_RANGE: [number, number] = [0.6, 1.4];
/** How far the key light is allowed to take the estimate's hue. */
const TINT_STRENGTH = 0.35;

/** Rec. 709 luminance — the standard weighting for perceived brightness. */
export function luminance(r: number, g: number, b: number): number {
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Map an unbounded radiance to a multiplier around 1.
 *
 * The curve is `x / (x + k)` normalised so that `reference` maps to 1 — a
 * saturating response, so a reading ten times too bright is only slightly
 * brighter rather than ten times so. That property is the whole point: the
 * failure mode of a linear mapping is a white screen.
 */
export function radianceToScale(
  value: number,
  reference: number,
  range: [number, number],
): number {
  if (!Number.isFinite(value) || value <= 0 || reference <= 0) return 1;
  const soft = (x: number) => x / (x + reference);
  const scaled = soft(value) / soft(reference);
  return clamp(scaled, range[0], range[1]);
}

/**
 * The ambient term from the DC band of a spherical-harmonics estimate.
 *
 * Only the first three coefficients are read. They are the L0 band — the
 * average light arriving from every direction — which is exactly what a
 * hemisphere light approximates. The higher bands describe directionality we
 * already get, better, from `primaryLightDirection`.
 *
 * `0.886227` is π·Y₀₀: the factor that turns an L0 coefficient into
 * irradiance. Without it the number is off by a constant, which would not
 * matter here (everything is normalised against a reference) but would make
 * the reference a magic number rather than a measurable one.
 */
export function ambientFromHarmonics(
  coefficients: ArrayLike<number> | null | undefined,
  reference: number,
): number {
  if (!coefficients || coefficients.length < 3) return 1;
  const k = 0.886227;
  const value = luminance(
    coefficients[0] * k,
    coefficients[1] * k,
    coefficients[2] * k,
  );
  return radianceToScale(value, reference, AMBIENT_RANGE);
}

/**
 * Normalise a radiance triple to a hue, then pull it most of the way back
 * toward white.
 *
 * Taking the measured colour at full strength turns the whole garden the
 * colour of whatever lamp is nearest. A third of the way is enough to read as
 * "lit by this room" without recolouring the planting.
 */
export function tintFromRadiance(
  r: number,
  g: number,
  b: number,
): [number, number, number] {
  const peak = Math.max(r, g, b);
  if (!Number.isFinite(peak) || peak <= 0) return [1, 1, 1];
  const hue: [number, number, number] = [r / peak, g / peak, b / peak];
  return [
    1 + (hue[0] - 1) * TINT_STRENGTH,
    1 + (hue[1] - 1) * TINT_STRENGTH,
    1 + (hue[2] - 1) * TINT_STRENGTH,
  ];
}

/** Normalise a direction, returning null if it is degenerate. */
export function normalise(
  x: number,
  y: number,
  z: number,
): [number, number, number] | null {
  const length = Math.hypot(x, y, z);
  if (!Number.isFinite(length) || length < 1e-6) return null;
  return [x / length, y / length, z / length];
}

/** Neutral lighting — what every caller falls back to. */
export function neutralSample(): LightSample {
  return { direction: null, sun: 1, ambient: 1, colour: [1, 1, 1] };
}

/**
 * The reference radiance that maps to "no change".
 *
 * Chosen so an ordinary bright day lands near 1 rather than at a clamp, which
 * is the condition this was tested in and the condition most players will
 * be in. Indoors reads lower and the garden dims a little, which is correct.
 */
export const REFERENCE_RADIANCE = 3;

/**
 * Read an `XRLightEstimate` into our units.
 *
 * Typed structurally rather than against `XRLightEstimate` so this file has no
 * WebXR dependency and can be exercised without a headset.
 */
export function sampleFrom(estimate: {
  primaryLightDirection?: { x: number; y: number; z: number } | null;
  primaryLightIntensity?: { x: number; y: number; z: number } | null;
  sphericalHarmonicsCoefficients?: ArrayLike<number> | null;
} | null | undefined): LightSample {
  if (!estimate) return neutralSample();

  const dir = estimate.primaryLightDirection;
  const lit = estimate.primaryLightIntensity;

  const direction = dir ? normalise(dir.x, dir.y, dir.z) : null;
  const sun = lit
    ? radianceToScale(luminance(lit.x, lit.y, lit.z), REFERENCE_RADIANCE, SUN_RANGE)
    : 1;
  const colour = lit ? tintFromRadiance(lit.x, lit.y, lit.z) : ([1, 1, 1] as const);

  return {
    direction,
    sun,
    ambient: ambientFromHarmonics(
      estimate.sphericalHarmonicsCoefficients,
      REFERENCE_RADIANCE,
    ),
    colour: [colour[0], colour[1], colour[2]],
  };
}

/**
 * Frame-rate independent ease toward a target.
 *
 * The repo's convention, and it matters more here than anywhere: a light
 * estimate updates every frame and jitters, so applied directly the garden
 * would flicker. Easing at this rate takes roughly a second to settle, which
 * reads as the sun coming out rather than as a bug.
 */
export function ease(current: number, target: number, dt: number, k = 0.08): number {
  return current + (target - current) * (1 - Math.pow(k, dt));
}
